import {jsx} from 'snabbdom-jsx-lite';

import {BannerEl} from 'common/components/banner';
import {ActionEvent, BannerAction, BannerSize, BannerType} from 'common/components/banner/types';
import {Store} from 'common/report/stores/store';
import {SavedReportsLimit} from 'common/report/stores/saved-reports-limit';
import {trackUpsellEvent} from 'common/report/util/tracking-helpers';
import {prompt} from 'common/components/confirm';
import {THEME} from 'common/components/confirm/types';
import {savedReportLimitInfo, savedReportsLimitStore} from 'common/report/stores/saved-reports-limit';

const PRICING_HREF = `/pricing/?utm_source=number_reports_gate`;

export function savedReportsLimitBannerRenderer(
  savedReportsLimitStore: Store<Record<string, SavedReportsLimit>>,
  projectId: number,
  bannerClass = ``,
) {
  const savedReportsLimit: SavedReportsLimit = savedReportsLimitStore.value[projectId] ?? {
    hasFiniteLimit: false,
    isBelowLimit: false,
  };
  if (savedReportsLimit.hasFiniteLimit) {
    const bannerSel = bannerClass ? `.${bannerClass}` : ``;
    return (
      <BannerEl
        sel={bannerSel}
        attrs={{
          theme: savedReportsLimit.isBelowLimit ? `info` : `warn`,
          type: BannerType.Embedded,
          size: BannerSize.Medium,
          'header-text': `${savedReportsLimit.savedReportsCount} of ${savedReportsLimit.finiteLimit} Saved Reports`,
          'body-text': `Your current plan allows ${savedReportsLimit.finiteLimit} saved reports per user per project. Upgrade your plan to save unlimited reports.`,
          ctas: [
            {
              actionName: BannerAction.Upgrade,
              text: `Upgrade`,
              icon: `share`,
              href: PRICING_HREF,
            },
          ],
          'style-override': `.banner-wrapper .banner-panel .banner-copy {width: calc(100% - 130px)}`,
          'banner-tracking-type': `Saved Reports Limit`,
        }}
        on={{
          action: (ev: ActionEvent) => {
            if (ev.detail.actionName === BannerAction.Upgrade) {
              trackUpsellEvent(`Click Upsell`, `bookmark`);
            }
          },
        }}
      />
    );
  }

  return null;
}

export function canSaveNewReports(
  savedReportsLimitStore: Store<Record<string, SavedReportsLimit>>,
  projectId: number,
): boolean {
  return savedReportsLimitStore.value[projectId]?.isBelowLimit ?? false;
}

export async function promptSaveReportLimitModal(projectId, newReport = true) {
  const {isBelowLimit} = savedReportLimitInfo(projectId);
  const canSaveReport = isBelowLimit || !newReport;
  const promptResponse = await prompt(
    {
      closeable: false,
      'confirm-button-label': canSaveReport ? `Save Report` : `Upgrade`,
      open: true,
      theme: canSaveReport ? THEME.INFO : THEME.WARN,
      title: canSaveReport ? `Save Report` : `Saved Report Limit Reached`,
    },
    {bannerContent: savedReportsLimitBannerRenderer(savedReportsLimitStore, projectId, `banner`)},
  );

  const {didConfirm} = promptResponse;
  if (didConfirm && !canSaveReport) {
    window.open(PRICING_HREF);
  }
}
